// Display mirror of the row-dependent permission rules in the backend. The server enforces
// them (backend/src/routes/improvement.routes.ts actionPermissions,
// backend/src/routes/submissions.routes.ts permissionForChange); these only decide which
// buttons a page offers, so they must follow the server, never lead it.
//
// The permission list a page reads (user.permissions) already has implied permissions
// expanded by the API (middleware/permissions.ts IMPLIED_BY), so a check for
// "resilience:write" is true for anyone holding controls:write.

type Perms = readonly string[] | undefined | null;

const holds = (perms: Perms, any: readonly string[]) => !!perms && any.some((p) => perms.includes(p));

/** Every permission that may write some corrective action. */
export const ACTION_WRITERS = ["controls:write", "risks:write", "tprm:write", "resilience:write", "legal:write", "privacy:write", "audit:write"] as const;

export interface ActionShape {
  source?: string | null;
  riskId?: string | null;
  supplierId?: string | null;
  incidentId?: string | null;
}

/** Who may write this action: controls:write always, plus the owner of what it fixes. */
export function actionPermissions(a: ActionShape): string[] {
  const perms = ["controls:write"];
  if (a.source === "risk" || a.riskId) perms.push("risks:write");
  if (a.supplierId) perms.push("tprm:write");
  if (a.source === "incident" || a.source === "exercise" || a.incidentId) perms.push("resilience:write");
  if (a.source === "segmentation" || a.source === "firewall" || a.source === "physical_security") perms.push("resilience:write");
  if (a.source === "legal") perms.push("legal:write");
  if (a.source === "data_protection") perms.push("privacy:write");
  if (a.source === "internal_audit") perms.push("audit:write");
  return perms;
}

export const canWriteAction = (perms: Perms, a: ActionShape) => holds(perms, actionPermissions(a));
export const canWriteAnyAction = (perms: Perms) => holds(perms, ACTION_WRITERS);

/** Statuses that are the record of what an authority was told. */
const SENT = new Set(["submitted", "acknowledged"]);

/** Whether this change to a submission is sending it (submissions:send) rather than preparing it. */
export const isSendingChange = (from: string, to?: string) => SENT.has(from) || (to !== undefined && SENT.has(to));

export function canChangeSubmission(perms: Perms, from: string, to?: string): boolean {
  return holds(perms, [isSendingChange(from, to) ? "submissions:send" : "submissions:prepare"]);
}

/**
 * What each permission lets a member do, in the words the server's refusals use (mirror of backend
 * middleware/permissions.ts PERMISSION_WORDS, kept identical by lib/__tests__/permissionWords.test.ts). A page
 * that hides a form says who can use it in these words, never by the permission key.
 */
export const PERMISSION_WORDS: Record<string, string> = {
  "controls:write": "change the assessment of your standards",
  "risks:write": "change the risk register",
  "tprm:write": "change the supplier register",
  "documents:write": "add or change documents and evidence",
  "compliance:run": "run compliance checks and AI analysis",
  "privacy:write": "change the data protection registers",
  "resilience:write": "change the incident and resilience registers",
  "legal:write": "change legal obligations",
  "audit:write": "record internal audits",
  "submissions:prepare": "prepare regulatory submissions",
  "submissions:send": "record a submission as sent",
  "reports:generate": "generate the board report",
  "org:admin": "administer the organisation",
  "integrations:admin": "manage connections and integrations",
  "billing:view": "see billing and services",
  "people:write": "manage joiners, movers and leavers",
  "remediation:run": "run remediation",
  "org:delete": "delete the organisation",
  "group:view": "see the group's organisations",
  "group:admin": "act in the group's organisations",
  "firm:admin": "administer the consultancy",
  "firm:users": "manage the consultancy's staff",
};

/** "Only a member whose role allows them to change the supplier register can do this." */
export function whoCan(permission: string, what = "do this"): string {
  return `Only a member whose role allows them to ${PERMISSION_WORDS[permission] ?? permission} can ${what}.`;
}

export const holdsPermission = (perms: Perms, permission: string) => holds(perms, [permission]);
